import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, Pencil } from "lucide-react";
import { Badge, Button, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import { extractErrorMessage } from "@/api/client";
import { dealsApi } from "@/api/deals";
import {
  formatConsultingRatePercent as formatPercent,
  consultingRateToFraction as toFraction,
  consultingRateToPercentInput as toPercentInput,
} from "@/lib/buyerConsultingFee";
import {
  dealDetailKeys,
  useConfirmBuyerConsultingFeeAuthorization,
  useUpdateBuyerConsultingFeeAuthorization,
} from "@/hooks/deals/useDealDetail";
import {
  DEAL_PERMISSIONS,
  type BuyerConsultingFeeAuthorizationDto,
} from "@/types/deals";

interface Props {
  deal: { id: string };
}

/**
 * The one line that keeps this fee from being read as a charge on top of the
 * buyer's payment. Rendered on EVERY state, including when both legs are 0% —
 * the disambiguation is what the line is for, not a footnote about a value
 * that happens to be non-zero. (The seller→broker note on the broker card
 * above plays exactly this role for that fee.)
 */
const CARVE_NOTE =
  "Carved from the seller's distribution — the buyer's payment amount does not change.";

/** What each leg bills on, in the deal-side vocabulary the engine's own
 *  obligation-type descriptions use. */
const MONTHLY_BASIS = "Of each scheduled principal & interest payment — never the balloon.";
const LIQUIDITY_BASIS =
  "Of the gross remitted at payoff, balloon, refinance or full prepayment.";

// The three unit helpers moved VERBATIM to @/lib/buyerConsultingFee (same
// bodies, same names re-aliased on import here, so this card's behaviour is
// byte-identical) so the loan-side terms card shares them instead of adding
// another per-file copy. This repo has no shared percent formatter and a long
// tail of local ones, two of which export the same NAME with opposite unit
// contracts — the two surfaces that render THIS fee must not join that tail
// and must never disagree with each other. See the lib file's own header.

/**
 * Buyer Consulting Fee Authorization — Phoenix's own consulting fee on the
 * deal, in two INDEPENDENT legs: a percentage of each scheduled monthly
 * payment, and a percentage of the gross remitted at a liquidity event
 * (balloon, payoff, refinance, full prepayment). Loan administrators
 * (Deals.Update) edit and confirm the terms; editing resets verification
 * server-side, so the card returns to "Not yet verified" after an edit.
 * Self-gated on Deals.Read — renders nothing when the user can't read.
 *
 * 🔴 There is no mode: a leg is on or off by being non-zero or zero, and
 * either may be zero while the other is not. Do not model this on the broker
 * card's mode selector.
 */
export function BuyerConsultingFeeAuthorizationCard({ deal }: Props) {
  const { hasPermission } = useAuth();
  const fmt = useDateFormatter();
  const canRead = hasPermission(DEAL_PERMISSIONS.Read);
  const canEdit = hasPermission(DEAL_PERMISSIONS.Update);
  const [editOpen, setEditOpen] = useState(false);

  const confirmMutation = useConfirmBuyerConsultingFeeAuthorization(deal.id);
  const toast = useToast();

  const query = useQuery({
    queryKey: dealDetailKeys.buyerConsultingFeeAuthorization(deal.id),
    queryFn: () => dealsApi.buyerConsultingFeeAuthorization(deal.id),
    enabled: canRead,
  });

  // Section is visible only to users who can read the deal.
  if (!canRead) return null;

  const dto = query.data;

  return (
    <section className="rounded-lg border border-border-light bg-white-pure p-space-4">
      <div className="flex items-center justify-between mb-space-3">
        <h2 className="font-semibold text-onyx m-0">Buyer consulting fee authorization</h2>
        {canEdit && dto && (
          <Button
            size="sm"
            variant="tertiary"
            onClick={() => setEditOpen(true)}
            className="gap-space-1"
          >
            <Pencil size={14} aria-hidden />
            Edit terms
          </Button>
        )}
      </div>

      {query.isLoading ? (
        <p className="text-small text-gray-mid italic m-0">Loading…</p>
      ) : !dto ? (
        <p className="text-small text-gray-mid m-0">
          Buyer consulting fee authorization is not available for this deal.
        </p>
      ) : (
        <>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-space-3 m-0">
            <Component label="Monthly fee">
              <span className="text-onyx">{formatPercent(dto.monthlyFeePct)}</span>
              <p className="text-caption text-gray-mid m-0 mt-space-1">
                {dto.monthlyFeePct === 0 ? "Not charged on this deal." : MONTHLY_BASIS}
              </p>
            </Component>

            <Component label="Liquidity event fee">
              <span className="text-onyx">{formatPercent(dto.liquidityFeePct)}</span>
              <p className="text-caption text-gray-mid m-0 mt-space-1">
                {dto.liquidityFeePct === 0 ? "Not charged on this deal." : LIQUIDITY_BASIS}
              </p>
            </Component>
          </dl>

          <p className="text-caption text-gray-mid m-0 mt-space-3">{CARVE_NOTE}</p>

          <div className="mt-space-4 pt-space-3 border-t border-border-light flex items-center justify-between gap-space-3 flex-wrap">
            {dto.isVerified ? (
              <div className="flex items-center gap-space-2">
                <Badge status="finalized" className="gap-space-1">
                  <BadgeCheck size={12} aria-hidden />
                  Verified
                </Badge>
                <span className="text-small text-gray-mid">
                  by {dto.verifiedByName ?? "—"}
                  {dto.verifiedAt ? ` on ${fmt.date(dto.verifiedAt)}` : ""}
                </span>
              </div>
            ) : (
              <span className="text-small text-gray-mid italic">Not yet verified</span>
            )}

            {/* No canVerify gate — unlike the broker fee there is nothing here
                that can be half-specified, so confirmation is never blocked. */}
            {canEdit && !dto.isVerified && (
              <Button
                size="sm"
                onClick={() =>
                  confirmMutation.mutate(undefined, {
                    onSuccess: () => toast.success("Buyer consulting fee terms confirmed."),
                    onError: (err) => toast.error(extractErrorMessage(err)),
                  })
                }
                loading={confirmMutation.isPending}
              >
                Confirm terms
              </Button>
            )}
          </div>
        </>
      )}

      {dto && (
        <BuyerConsultingFeeAuthorizationEditModal
          open={editOpen}
          dealId={deal.id}
          dto={dto}
          onClose={() => setEditOpen(false)}
        />
      )}
    </section>
  );
}

function Component({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="font-display text-caption uppercase tracking-label text-gray-mid">
        {label}
      </dt>
      <dd className="text-small m-0 mt-[2px]">{children}</dd>
    </div>
  );
}

interface ModalProps {
  open: boolean;
  dealId: string;
  dto: BuyerConsultingFeeAuthorizationDto;
  onClose: () => void;
}

/**
 * Edit form for the two consulting-fee percentages. The UI works in PERCENT
 * but the API stores FRACTIONS, so both figures are ×100 on load and ÷100 on
 * save. Saving resets verification server-side.
 *
 * The cap comes from the server's own `maxRateFraction` rather than a second
 * hardcoded 15, and is compared with `<=` and NO epsilon — the same exact
 * comparison the server makes on `decimal`. Each leg is checked
 * independently; there is deliberately no combined cap.
 */
function BuyerConsultingFeeAuthorizationEditModal({ open, dealId, dto, onClose }: ModalProps) {
  const toast = useToast();
  const mutation = useUpdateBuyerConsultingFeeAuthorization(dealId);

  // Both fields hold a PERCENT (2.5), never the stored fraction.
  const [monthlyPercent, setMonthlyPercent] = useState("");
  const [liquidityPercent, setLiquidityPercent] = useState("");

  // Re-seed the form from the current terms whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    setMonthlyPercent(toPercentInput(dto.monthlyFeePct));
    setLiquidityPercent(toPercentInput(dto.liquidityFeePct));
  }, [open, dto]);

  const capPct = 100 * dto.maxRateFraction;

  // Blank is NOT read as 0 — these are money rates, so the figure has to be
  // stated rather than inferred from an empty box.
  function legValid(raw: string): boolean {
    const trimmed = raw.trim();
    if (trimmed === "") return false;
    const n = Number(trimmed);
    return Number.isFinite(n) && n >= 0 && n <= capPct;
  }

  const monthlyValid = legValid(monthlyPercent);
  const liquidityValid = legValid(liquidityPercent);
  const valid = monthlyValid && liquidityValid;

  function handleSave() {
    mutation.mutate(
      {
        monthlyFeePct: toFraction(Number(monthlyPercent.trim())),
        liquidityFeePct: toFraction(Number(liquidityPercent.trim())),
      },
      {
        onSuccess: () => {
          toast.success("Buyer consulting fee terms updated.");
          onClose();
        },
        onError: (err) => toast.error(extractErrorMessage(err)),
      },
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Edit buyer consulting fee terms"
      description="Confirming these terms is a separate step."
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!valid} loading={mutation.isPending}>
            Save terms
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        <p className="text-small text-gray-mid m-0">
          Saving updates the terms and resets verification — confirm the terms again afterwards.
        </p>

        <FormField
          label="Monthly fee (%)"
          htmlFor="bcfa-monthly"
          helperText={`Of each scheduled principal & interest payment. Entered as a percent (e.g. 2.5 for 2.5%); at most ${capPct}%. Enter 0 if this leg isn't charged.`}
        >
          <Input
            id="bcfa-monthly"
            type="number"
            min="0"
            max={String(capPct)}
            step="0.01"
            inputMode="decimal"
            value={monthlyPercent}
            onChange={(e) => setMonthlyPercent(e.target.value)}
            placeholder="0"
          />
        </FormField>

        <FormField
          label="Liquidity event fee (%)"
          htmlFor="bcfa-liquidity"
          helperText={`Of the gross remitted at payoff, balloon, refinance or full prepayment. Entered as a percent; at most ${capPct}%. Enter 0 if this leg isn't charged.`}
        >
          <Input
            id="bcfa-liquidity"
            type="number"
            min="0"
            max={String(capPct)}
            step="0.01"
            inputMode="decimal"
            value={liquidityPercent}
            onChange={(e) => setLiquidityPercent(e.target.value)}
            placeholder="0"
          />
        </FormField>

        <p className="text-caption text-gray-mid m-0">{CARVE_NOTE}</p>

        {/* A disabled Save with nothing said strands the reader — name the way
            back rather than leaving them to guess which figure is refused. */}
        {!valid && (
          <p role="alert" className="text-caption text-status-error m-0">
            Enter each percentage as a number between 0 and {capPct}%.
          </p>
        )}
      </div>
    </Modal>
  );
}
